import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ScanLine, Shuffle, Sparkles } from 'lucide-react';
import { etatBandeau } from '../../utils/besoins';
import { CIBLE_CARTES, CIBLE_VARIE } from '../../utils/compositionAuto';
import { formatJourMois as jourMois, pluriel, texteLisible } from './formatConfig';
import { TexteFormules } from './ApercuExercice';

const BOUTON_SPARKLES = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-500 text-blue-700 text-xs font-bold hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 transition';
const LIEN = 'font-semibold text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded';
const ITEM = 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm font-semibold text-gray-700 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400';

const VERSIONS = [
  { version: CIBLE_CARTES, Icone: ScanLine, libelle: 'Pour cartes QR (QCM)' },
  { version: CIBLE_VARIE, Icone: Shuffle, libelle: 'Pour papier ou en ligne (tous types)' },
];

/**
 * Diagnostic en deux versions (R5 amendée le 2026-10-03) : on choisit avant de
 * composer. `onChoisir(CIBLE_CARTES | CIBLE_VARIE)`. Liste dépliante (motif
 * « disclosure ») : Échap, clic dehors ou sortie au clavier la referment. Le
 * focus revient au déclencheur, que la boîte de remplacement (F6) rendra.
 */
const MenuDiagnostic = ({ libelle, className, icone = null, onChoisir }) => {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef(null);
  const declencheur = useRef(null);
  const idListe = useId();
  useEffect(() => {
    if (!ouvert) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setOuvert(false); declencheur.current?.focus(); } };
    const onClic = (e) => { if (!racine.current?.contains(e.target)) setOuvert(false); };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClic);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClic);
    };
  }, [ouvert]);
  const choisir = (version) => { setOuvert(false); declencheur.current?.focus(); onChoisir?.(version); };
  const onBlur = (e) => { if (!racine.current?.contains(e.relatedTarget)) setOuvert(false); };
  return (
    <div ref={racine} className="relative" onBlur={onBlur}>
      <button ref={declencheur} type="button" className={className} aria-expanded={ouvert} aria-controls={idListe} onClick={() => setOuvert((v) => !v)}>
        {icone}
        {libelle}
        <ChevronDown size={12} />
      </button>
      {ouvert && (
        <ul id={idListe} aria-label={libelle} className="absolute left-0 top-full mt-1.5 z-40 w-80 bg-white rounded-xl border border-gray-200 shadow-xl p-1.5">
          {VERSIONS.map(({ version, Icone, libelle: texte }) => (
            <li key={version}>
              <button type="button" className={ITEM} onClick={() => choisir(version)}>
                <Icone size={15} className="flex-shrink-0" />
                {texte}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Bandeau de l'attendu (F2, R12) : ce que TanoEd sait déjà de cet attendu pour
 * la classe, calculé à l'affichage depuis `bilan-attendus?cible=`. D, C ou A ;
 * en B (pas d'arbre, rien de mesuré) ou sans bilan, rien du tout. Jamais
 * bloquant : les exerciseurs restent affichés dessous.
 * ✦ Préparer les groupes : sur l'attendu en D (R7b), sur les prérequis en C (R7a) ;
 * « Recomposer les groupes » quand une session de groupes est en cours pour
 * cet attendu (`groupesEnCours`, D5).
 * Un diagnostic ou des groupes en cours sur un autre attendu (`sessionAilleurs`
 * = { type, cible }) : une séance à la fois, les boutons ✦ s'effacent et une
 * ligne ramène à la cible ; l'information reste (épreuve de Musk, 2026-10-04 :
 * le bouton invitait à écraser la séance, la confirmation rattrapait).
 */
const BandeauAttendu = ({ bilan, groupesEnCours = false, sessionAilleurs = null, onComposerDiagnostic, onOuvrir, onPreparerGroupes, onRevenirSession }) => {
  if (!bilan) return null;
  const { etat, nbEleves, nbAvecResultat, compteurs, date, fragiles } = etatBandeau(bilan);
  const actions = !sessionAilleurs;
  const ligneSession = sessionAilleurs && (
    <p className="flex flex-wrap items-baseline gap-x-1.5 text-slate-600">
      <Sparkles size={13} className="self-center text-blue-600" />
      <span>
        {sessionAilleurs.type === 'groupes' ? 'Groupes de besoin en cours sur' : 'Diagnostic en cours sur'}
        {' '}<TexteFormules className="font-semibold text-slate-800">{sessionAilleurs.cible?.description || ''}</TexteFormules>
      </span>
      <span aria-hidden="true">·</span>
      <button type="button" className={LIEN} onClick={() => onRevenirSession?.()}>Revenir</button>
    </p>
  );
  if (etat === 'B') {
    return ligneSession ? (
      <section aria-label="Ce que TanoEd sait de cet attendu" className="block rounded-xl border border-blue-100 bg-white/80 px-4 py-3 text-sm text-slate-700">
        {ligneSession}
      </section>
    ) : null;
  }
  const description = (id) => (bilan.attendus || []).find((a) => a.attendu_id === id)?.description || `Attendu ${id}`;

  const boutonGroupes = (base, libelle) => (
    <button type="button" className={BOUTON_SPARKLES} onClick={() => onPreparerGroupes?.(base)}>
      <Sparkles size={13} />
      {groupesEnCours ? 'Recomposer les groupes' : libelle}
    </button>
  );

  let contenu;
  if (etat === 'D') {
    contenu = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 min-w-[16rem]">
          Dernière mesure ({jourMois(date)}) : {compteurs.a_renforcer} à renforcer · {compteurs.en_cours} en cours
          {' · '}{compteurs.maitrise} maîtrisé · {compteurs.sans_donnee} sans résultat
        </p>
        {actions && boutonGroupes('attendu', 'Préparer les groupes de besoin')}
      </div>
    );
  } else if (etat === 'C') {
    contenu = (
      <>
        <p>
          Prérequis mesurés (dernière mesure {jourMois(date)}) : {fragiles.length === 0 ? 'aucun fragile' : pluriel(fragiles.length, 'fragile')}
        </p>
        {fragiles.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {fragiles.map((f) => (
              <li key={f.attenduId} className="flex flex-wrap items-baseline gap-x-1.5">
                <TexteFormules className="text-slate-800">{description(f.attenduId)}</TexteFormules>
                <span className="text-slate-500">· {f.nbARenforcer} à renforcer ·</span>
                <button type="button" className={LIEN} onClick={() => onOuvrir?.(f.attenduId)} aria-label={`ouvrir ${texteLisible(description(f.attenduId))}`}>ouvrir</button>
              </li>
            ))}
          </ul>
        )}
        {actions && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {boutonGroupes('prerequis', 'Préparer les groupes sur les prérequis')}
            <MenuDiagnostic libelle="Refaire le diagnostic" className={`inline-flex items-center gap-1 py-1 text-xs ${LIEN}`} onChoisir={onComposerDiagnostic} />
          </div>
        )}
      </>
    );
  } else {
    contenu = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 min-w-[16rem]">
          Pas assez de résultats sur cet attendu ni sur ses prérequis ({pluriel(nbAvecResultat, 'élève')} sur {nbEleves}).
        </p>
        {actions && <MenuDiagnostic libelle="Composer le diagnostic" className={BOUTON_SPARKLES} icone={<Sparkles size={13} />} onChoisir={onComposerDiagnostic} />}
      </div>
    );
  }

  // `block` : index.css fait de toute <section> une rangée flex centrée.
  return (
    <section
      aria-label="Ce que TanoEd sait de cet attendu"
      className="block rounded-xl border border-blue-100 bg-white/80 px-4 py-3 text-sm text-slate-700"
    >
      {ligneSession && <div className="mb-2 pb-2 border-b border-blue-50">{ligneSession}</div>}
      {contenu}
    </section>
  );
};

export default BandeauAttendu;
